import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import { Observable, ReplaySubject } from 'rxjs';
import { filter, map, takeWhile, withLatestFrom } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import * as fromAuth from '../../auth/store/auth.reducer';
import * as fromDataServices from '../../data-services';
import * as fromLookups from '../../lookups';
import { ScheduledDate } from '../../lookups/store/schedule-date.model';
import { Process } from '../../shared';
import { UITreeNode } from '../../shared/efaact-primeng.module';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromUser from '../../user-info';
import { IUserInfo } from '../../user-info';
import { ReportingComponent } from '../presenters/reporting.component';
import * as fromReports from '../store';
import { ReportParams } from '../store/report-params.model';
import { ReportFavoriteParams } from '../store/favorite-params.model';

@Component({
    selector: 'ef-reporting-feature',
    templateUrl: './reporting-feature.component.html',
    styleUrls: ['./reporting-feature.component.scss'],
    standalone: false
})
export class ReportingFeatureComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    private auth$: Observable<fromAuth.State>;
    public maxReports$: Observable<number>;
    public userInfo$: Observable<IUserInfo>;
    public reports$: Observable<Array<UITreeNode>>;
    public scheduledDates$: Observable<Array<ScheduledDate>>;
    public directReports$: Observable<Array<fromLookups.IDirectReport>>;
    public costCenters$: Observable<Array<fromLookups.IUserGroup>>;
    public contracts$: Observable<Array<fromDataServices.Contract.Contract>>;
    public process$: Observable<Process>;
    public selectedScheduledDate: ScheduledDate;
    private RunReportsSubject = new ReplaySubject<{
        reports: Array<fromReports.Report>;
        queryparams: any;
        params: ReportParams;
    }>(1);
    private active = true;
    popup: Window;
    environment: any;
    contractDataService: EntityCollectionService<fromDataServices.Contract.Contract>;
    @ViewChild('reportingPresenter', { static: false })
    reportingPresenter: ReportingComponent;
    constructor(
        public entityServices: EntityServices,
        private log: LoggingService,
        private store: Store<fromReports.State>
    ) {
        this.environment = environment;
        this.contractDataService =
            entityServices.getEntityCollectionService<fromDataServices.Contract.Contract>(
                fromDataServices.Contract.entityName
            );
    }

    ngOnInit() {
        this.setupObservables();
    }
    ngOnDestroy() {
        this.active = false;
    }
    ngAfterViewInit(): void {
        // this.contractDataService.getAll();
        this.setupSubscriptions();
    }

    setupObservables() {
        this.reports$ = this.store.pipe(
            select(fromReports.selectAll),
            map(reports => {
                // Create a set of favorited report IDs for quick lookup
                const favoritedReportIds = new Set(
                    reports.filter(r => r.CatID === 1).map(r => r.RptID)
                );
                
                const groupsIds = Array.from(
                    reports.reduce((a, c) => a.add(c.CatID), new Set<number>())
                );
                const groups = groupsIds.reduce(
                    (a, c) => [
                        ...a,
                        {
                            label: reports.find(r => r.CatID === c)
                                .CatDescription,
                            rowNode: c,
                            key: c,
                            expanded: c === 1 // Expand the first group by default
                        }
                    ],
                    []
                );
                this.log.verbose({
                    type: 'ReportingFeatureComponent:reports$',
                    groups
                });
                groups.forEach(group => {
                    group.children = reports
                        .filter(r => r.CatID === group.rowNode)
                        .map(r => ({
                            label: `${r.RptNbr} - ${r.RptTitle}`,
                            rowNode: r,
                            key: `${r.RptNbr}_${group.rowNode}`, // Ensure unique keys across categories
                            data: {
                            ...r,
                            isFavorite: favoritedReportIds.has(r.RptID) // Mark as favorite if this RptID exists in favorites
                        }
                        }));
                    this.log.verbose({
                        type: 'ReportingFeatureComponent:reports$',
                        group
                    });
                });
                this.log.verbose({
                    type: 'ReportingFeatureComponent:reports$',
                    groups
                });
                return groups;
            })
        );
        this.userInfo$ = this.store.pipe(select(fromUser.getUserInfo));
        this.process$ = this.store.pipe(
            select(fromUser.getUserProcessesMainByName, {
                ProcessName: 'Reports'
            })
        );
        this.directReports$ = this.store.pipe(
            select(fromLookups.getDirectReportsLookup),
            map(logins => logins.map(item => ({ ...item })))
        );
        this.costCenters$ = this.store.pipe(
            select(fromLookups.getUserGroupsLookup)
        );
        this.contracts$ = this.contractDataService.entities$;
        this.auth$ = this.store.pipe(select(fromAuth.getAuth));
        this.maxReports$ = this.store.pipe(select(fromUser.getMaxReports));
        this.scheduledDates$ = this.store.pipe(
            select(fromLookups.ScheduledDateSelectors.selectAll),
            filter(dates => !!dates && dates.length > 0)
        );
    }

    setupSubscriptions() {
        this.RunReportsSubject.pipe(withLatestFrom(this.auth$, this.userInfo$))
            .pipe(takeWhile(x => this.active))
            .subscribe(([{ reports, queryparams, params }, auth, userInfo]) => {
                for (
                    let i = 0;
                    i < reports.length && i < userInfo.MaxNbrofReports;
                    i++
                ) {
                    const report = reports[i];

                    this.store.dispatch(
                        fromReports.runReport({
                            report: {
                                report,
                                params: {
                                    ...params,
                                    EmployeeListID: userInfo.EmployeeListID,
                                    RptID: report.RptID
                                },
                                queryparams: {
                                    ...queryparams,
                                    User: userInfo.EmployeeListID,
                                    Cust: auth.CustomerID,
                                    GroupId: -1
                                }
                            }
                        })
                    );
                }
            });
    }
    onRunReports({
        reports,
        queryparams,
        params
    }: {
        reports: Array<fromReports.Report>;
        queryparams: any;
        params: any;
    }) {
        this.log.verbose({
            type: 'ReportingFeatureComponent:onRunReports',
            reports,
            queryparams,
            params
        });
        this.RunReportsSubject.next({ reports, queryparams, params });
    }
    clear() {
        this.reportingPresenter.clearReports();
    }
    onFavoriteToggle(favoriteData: { RptID: number; EmployeeListID: string; IsFavorite: boolean }): void {
        const favoriteParams: ReportFavoriteParams = {
            RptID: favoriteData.RptID,
            EmployeeListID: favoriteData.EmployeeListID,
            RptDelete: !favoriteData.IsFavorite
        };

        this.store.dispatch(fromReports.reportFavorite({
            report: favoriteParams
        }));
    }
}
